import { useCallback, useRef, useState } from 'react';

// Map a DataTable sort.property to a backend sort column via `fieldMap`.
// Warns and falls back to `fallback` when the property is not mapped, so a
// new sortable column that forgot its mapping surfaces loudly instead of
// silently sorting by the default.
export const resolveSortField = (sort, fieldMap, fallback) => {
    const property = sort?.property;
    const mapped = property ? fieldMap[property] : undefined;
    if (property && !mapped) {
        console.warn(
            `[usePaginatedSort] Unmapped sort property "${property}"; ` +
                `falling back to "${fallback}". Add it to the SORT_FIELD_MAP.`,
        );
    }
    return mapped ?? fallback;
};

// Shared pagination + server-side sort state for dashboard tables.
// Owns page / pageSize / sort / totalItems and the DataTable + Pagination
// handlers. `sort` is fed to DataTable with `external: true`; loaders map
// `sort.property` to a backend column. `applyPagination(meta)` syncs the
// server's authoritative page/total back after each fetch, and clamps the
// page down when the set has shrunk below the current offset (for example
// after a row leaves the filtered set), so the caller is never stranded on
// a page that no longer exists.
//
// `initialPage` / `initialPageSize` / `initialTotalItems` seed the state so a
// caller that already has the first page from a router loader can paint the
// correct summary immediately and skip a duplicate fetch on mount.
export const usePaginatedSort = ({
    initialSort = { property: 'name', direction: 'asc' },
    stepOptions = [25, 50, 100],
    initialPage = 1,
    initialPageSize,
    initialTotalItems = 0,
} = {}) => {
    const [page, setPage] = useState(initialPage);
    const [pageSize, setPageSize] = useState(initialPageSize ?? stepOptions[0]);
    const [sort, setSort] = useState(initialSort);
    const [totalItems, setTotalItems] = useState(initialTotalItems);

    // Read by `applyPagination` without widening its dependency array, so
    // the callback keeps a stable identity for consumers' effect deps.
    const pageSizeRef = useRef(pageSize);
    pageSizeRef.current = pageSize;

    const handleSort = ({ property, direction }) => {
        setSort({ property, direction });
        setPage(1);
    };

    const handlePageChange = ({ page: nextPage, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize !== newPageSize) {
            setPageSize(newPageSize);
            setPage(1);
            return;
        }
        setPage(nextPage);
    };

    const handlePageSizeChange = ({ option }) => {
        setPageSize(option);
        setPage(1);
    };

    // Stable identity (only state setters in the closure) so consumers can
    // safely list it in their fetch effect's dependency array.
    const applyPagination = useCallback((meta) => {
        if (!meta?.pagination) return;

        const total = meta.pagination.total ?? 0;
        setTotalItems(total);

        const lastPage = Math.max(1, Math.ceil(total / pageSizeRef.current));
        setPage((currentPage) => {
            const serverPage = meta.pagination.page || currentPage;
            return Math.min(serverPage, lastPage);
        });
    }, []);

    return {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    };
};
